import CrudActionContainer from './CrudActionContainer';
import AdAccountModel from '../model/AdAccountModel';
import AdAccountController from '../controllers/AdAccountController';
import { Dispatch } from 'redux';
import { StoreState } from '../store-interfaces';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import { getActionHookBuilder } from './action-hook-builder';
import { isInvitedUserAuthError } from './ConnectedPageCRUDActions';

type RetrieveAll = (
  fbPageId: number | undefined,
  extended: boolean,
  next: string,
  all?: boolean,
) => (dispatch: Dispatch<StoreState>) => Promise<AdAccountModel[]>;

type Retrieve = (
  adAccountId: string,
  skipErrorHandling?: boolean,
) => (dispatch: Dispatch<StoreState>) => Promise<AdAccountModel>;

type AssignAdAccount = (
  fbPageId: number,
  adAccount: AdAccountModel,
  selected: boolean,
) => (dispatch: Dispatch<StoreState>) => Promise<AdAccountModel[]>;

type RetrieveByFbPageId = (
  fbPageId: number,
) => (dispatch: Dispatch<StoreState>) => Promise<AdAccountModel[]>;

class AdAccountCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('AD_ACCOUNTS');
    this.resetAction = super.makeActionKey('RESET');
  }

  retrieve: Retrieve = (adAccountId: string, skipErrorHandling?: boolean) => {
    return async dispatch => {
      try {
        dispatch(this.busy(this.retrieveAction));
        const response = await AdAccountController.retrieve(adAccountId);
        dispatch(this.retrieved(response.adAccount));
        return response.adAccount;
      } catch (e) {
        if (skipErrorHandling) {
          throw e;
        } else {
          return permissionsHandler(dispatch, this)(e);
        }
      } finally {
        dispatch(this.unbusy());
      }
    };
  };

  retrieveAll: RetrieveAll = (
    fbPageId: number | undefined,
    extended: boolean = false,
    next: string = '',
    all: boolean = false,
  ) => {
    return async dispatch => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await AdAccountController.retrieveAll(
          fbPageId,
          extended,
          next,
          all,
        );
        dispatch({
          type: this.retrievedAllAction,
          entities: response.data,
          next: response.next,
        });
        return response.data;
      } catch (e) {
        const res = await isInvitedUserAuthError(e);

        if (res) {
          dispatch(this.unbusy());
          return;
        }

        return permissionsHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  };

  retrieveByFbPageId: RetrieveByFbPageId = fbPageId => {
    return async dispatch => {
      try {
        return await AdAccountController.retrieveByFbPageId(fbPageId);
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  };

  assignAdAccount: AssignAdAccount = (
    fbPageId,
    adAccount: AdAccountModel,
    selected: boolean = false,
  ) => {
    return async dispatch => {
      try {
        const res = await AdAccountController.assignAdAccount(
          fbPageId,
          adAccount,
          selected,
        );

        await dispatch(this.retrieveByFbPageId(fbPageId));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  };
}

const adAccountCRUDActions = new AdAccountCRUDActions();

export const useAdAccountCRUDActions = getActionHookBuilder(
  adAccountCRUDActions,
  ['retrieveAll', 'retrieveByFbPageId', 'assignAdAccount'],
);

export default adAccountCRUDActions;
